import { useLocation } from "@solidjs/router";
import { For, Show, createMemo } from "solid-js";
import styles from "./Breadcrumbs.module.css";

interface Crumb {
  label: string;
  href?: string;
}

const SEGMENT_LABELS: Record<string, string> = {
  bots: "Bots",
  bot: "Bot",
  botengine: "Bot Engine",
  stats: "Stats",
  botplugins: "Bot Plugins",
  shopstock: "ShopStock",
  "online-players": "Online Players",
  dashboard: "Dashboard",
  mybots: "My Bots",
  mycommands: "My Commands",
  mygrants: "My Grants",
  dropconfigs: "Drop Configs",
  farm: "Farm",
  "orbs-trader": "Orbs Trader",
  "case-opening": "Case Opening",
  "pokeball": "Sammelball",
  biomes: "Biomes",
  inventory: "Inventory",
  storages: "Storages",
  audit: "Audit Log",
  auditlog: "Audit Log",
  auditlogs: "Audit Logs",
  items: "Items",
  ah: "Auction House",
  watches: "Watchlist",
  chatlogs: "Chat Logs",
  command: "Command",
  admin: "Admin",
  tenants: "Tenants",
  proxies: "Proxies",
  possessions: "Possessions",
  log: "Translate Log",
  eventviewer: "Event Viewer",
  oauthclients: "OAuth Clients",
  oauthclient: "OAuth Client",
  user: "User",
  grant: "Grant",
  tenant: "Tenant",
  settings: "Settings",
  members: "Members",
  me: "Account",
  jobs: "Jobs",
  "profile-history": "Profile History",
  new: "New",
};

function labelize(seg: string): string {
  if (SEGMENT_LABELS[seg]) return SEGMENT_LABELS[seg];
  if (/^[0-9a-f-]{8,}$/i.test(seg)) return "Details";
  return seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " ");
}

export function Breadcrumbs() {
  const location = useLocation();

  const crumbs = createMemo<Crumb[]>(() => {
    const path = location.pathname;
    if (path === "/" || path === "") return [];
    // Sellbot, payments and orbs trader pages carry their own tab bar, which already shows the current section.
    if (path === "/sellbot" || path.startsWith("/sellbot/")) return [];
    if (path === "/payments" || path.startsWith("/payments/")) return [];
    if (path === "/bot/orbs-trader" || path.startsWith("/bot/orbs-trader/")) return [];
    const segs = path.split("/").filter(Boolean);
    if (segs.length <= 1) return [];

    const out: Crumb[] = [];
    let acc = "";
    for (let i = 0; i < segs.length; i++) {
      acc += "/" + segs[i];
      const isLast = i === segs.length - 1;
      let href = isLast ? undefined : acc;
      // /bot/* paths are per-bot plugin pages; their list lives at /botplugins.
      if (href === "/bot") href = "/botplugins";
      // A schematic has no page of its own, only /schematics/:id/build, so its id crumb goes to the list.
      if (href?.startsWith("/schematics/")) href = "/schematics";
      out.push({ label: labelize(segs[i]), href });
    }
    return out;
  });

  return (
    <Show when={crumbs().length > 0}>
      <nav class={styles.crumbs} aria-label="Breadcrumb">
        <For each={crumbs()}>
          {(c, i) => (
            <>
              <Show when={i() > 0}>
                <i class={`fa-solid fa-chevron-right ${styles.sep}`} aria-hidden="true" />
              </Show>
              <Show when={c.href} fallback={<span class={styles.current}>{c.label}</span>}>
                <a href={c.href} class={styles.crumb}>{c.label}</a>
              </Show>
            </>
          )}
        </For>
      </nav>
    </Show>
  );
}
